import { db } from '../firebase';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';

/**
 * Save a user preference to Firebase
 * @param {string} shopId - The shop ID
 * @param {string} userId - The user ID
 * @param {string} preferenceKey - The key for the preference (e.g., 'sizechartChecker')
 * @param {object} preferenceValue - The value to save
 * @returns {Promise<void>}
 */
export const saveUserPreference = async (shopId, userId, preferenceKey, preferenceValue) => {
  try {
    if (!shopId || !userId) {
      console.error('Shop ID and User ID are required to save preferences');
      return;
    }

    console.log(`Saving preferences for shop ${shopId}, user ${userId}, key ${preferenceKey}:`, preferenceValue);
    
    // Create a document ID that combines shop and user
    const docId = `${shopId}_${userId}`;
    console.log('Document ID for preferences:', docId);
    
    const preferencesRef = doc(db, 'userPreferences', docId);
    const preferencesDoc = await getDoc(preferencesRef);

    if (preferencesDoc.exists()) {
      console.log('Updating existing preferences document');
      // Update existing document
      await updateDoc(preferencesRef, {
        [preferenceKey]: preferenceValue,
        updatedAt: new Date()
      });
    } else {
      console.log('Creating new preferences document');
      // Create new document
      await setDoc(preferencesRef, {
        shopId,
        userId,
        [preferenceKey]: preferenceValue,
        createdAt: new Date(),
        updatedAt: new Date()
      });
    }

    console.log('Successfully saved preferences');
    return true;
  } catch (error) {
    console.error('Error saving user preference:', error);
    throw error;
  }
};

/**
 * Get user preferences from Firebase
 * @param {string} shopId - The shop ID
 * @param {string} userId - The user ID
 * @param {string} preferenceKey - Optional key to get specific preference
 * @returns {Promise<object>} The user preferences
 */
export const getUserPreferences = async (shopId, userId, preferenceKey = null) => {
  try {
    if (!shopId || !userId) {
      console.error('Shop ID and User ID are required to get preferences');
      return null;
    }

    console.log(`Getting preferences for shop ${shopId}, user ${userId}${preferenceKey ? `, key ${preferenceKey}` : ''}`);
    
    // Create a document ID that combines shop and user
    const docId = `${shopId}_${userId}`;
    console.log('Document ID for preferences:', docId);
    
    const preferencesRef = doc(db, 'userPreferences', docId);
    const preferencesDoc = await getDoc(preferencesRef);

    if (!preferencesDoc.exists()) {
      console.log('No preferences document found');
      return null;
    }

    const data = preferencesDoc.data();
    console.log('Retrieved preferences data:', data);
    
    // Return specific preference if key provided
    if (preferenceKey) {
      const preferenceValue = data[preferenceKey] || null;
      console.log(`Preference value for key ${preferenceKey}:`, preferenceValue);
      return preferenceValue;
    }
    
    // Otherwise return all preferences
    return data;
  } catch (error) {
    console.error('Error getting user preferences:', error);
    return null;
  }
};
